import * as persistenceLayer from '#~/persistence/index.js';

const STORAGE_PREFIX = 'auto_link_v2_create_idempotency';

const storageKeyFor = (accountId: string, requestId: string): string =>
  `${STORAGE_PREFIX}:${accountId}:${requestId}`;

/**
 * Return the stable backend create key for one pending bridge-link request.
 * The value is deterministic even when persistence is unavailable; persisting
 * it documents the in-flight logical attempt across controller remounts.
 *
 * Per `settle-every-terminal-branch`: callers clear the record only after the
 * bridge link reaches verified success, never after an ambiguous create error.
 */
export const resolveAutoLinkCreateIdempotencyKey = async (
  accountId: string,
  requestId: string
): Promise<string> => {
  const storageKey = storageKeyFor(accountId, requestId);
  const deterministicKey = `auto-link:${requestId}`;

  try {
    const existing = await persistenceLayer.get(storageKey);
    if (existing) return existing;
  } catch (error) {
    console.warn(
      '[auto-link] Failed to read create idempotency key; using deterministic key',
      error
    );
  }

  try {
    await persistenceLayer.set(storageKey, deterministicKey);
  } catch (error) {
    console.warn(
      '[auto-link] Failed to persist create idempotency key; deterministic retry remains safe',
      error
    );
  }

  return deterministicKey;
};

/** Remove the persisted attempt key only after verified link success. */
export const clearAutoLinkCreateIdempotencyKey = async (
  accountId: string,
  requestId: string
): Promise<void> => {
  try {
    await persistenceLayer.remove(storageKeyFor(accountId, requestId));
  } catch (error) {
    console.warn('[auto-link] Failed to clear create idempotency key (non-fatal)', error);
  }
};
